/* eslint-disable jsx-a11y/accessible-emoji */
import React from 'react';
import PropTypes from 'prop-types';
import MomentUtils from '@date-io/moment';
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import { withStyles, Typography, Button } from '@material-ui/core';
import TextField from '@material-ui/core/TextField';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import Select from '@material-ui/core/Select';
import moment from 'moment';
import ContentHeader from './ContentHeader';
import {
    calc,
    isUnavailableFn,
    isAnyAvailable,
    isDateAvailable
} from '../helpers/service-availability';
import { getYearGroups } from '../helpers/ticket';
import ZonesForm from './ZonesForm';
import globalStyles from '../styles';

const styles = theme => ({
    root: {
        background: '#fff',
        boxShadow: '0px 2px 5px 0px rgba(0,0,0,0.21)'
    },
    form: {
        padding: '0 10px 10px'
    },
    description: {
        padding: '20px 5px 0',
        fontWeight: '100',
        textAlign: 'center'
    },
    name: {
        margin: '0 auto 20px'
    },
    input: {
        width: '100%',
        maxWidth: '250px',
        display: 'block',
        margin: '20px auto',
        '& > div': {
            display: 'block'
        },
        '& input': {
            width: 220
        }
    },
    button: {
        height: '56px',
        margin: '0 auto 10px'
    },
    uploadButton: {
        height: '56px',
        margin: '0 auto',
        padding: '0.6em'
    },
    confirmButton: {
        height: '56px',
        margin: '0 0 0 10px',
        padding: '0.6em'
    },
    upload: {
        display: 'none'
    },
    imageUpload: {
        margin: '0 0 40px 0',
        textAlign: 'center'
    },
    datePicker: {
        margin: '0 auto 20px',
        maxWidth: '250px',
        '& > div': {
            width: '100%'
        }
    },
    imagePreview: {
        minHeight: '56px',
        width: '100%',
        maxWidth: '400px',
        margin: '0 auto 10px',
        '& > img': {
            maxWidth: '100%'
        },
        '& > div': {
            background: 'lightgrey',
            minHeight: '56px',
            borderRadius: '4px',
            border: '1px solid'
        },
        [theme.breakpoints.down('sm')]: {
            marginTop: '10px'
        }
    },
    imagePreviewHelp: {
        display: 'block',
        fontWeight: '100',
        padding: '.5em'
    },
    imagePreviewPlaceholder: {
        borderRadius: '0 !important',
        minHeight: '400px !important',
        border: '0 !important',
        width: '252px',
        margin: '0 auto',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
    },
    photoHelp: {
        ...globalStyles.link,
        padding: '1em 0',
        display: 'block'
    },
    formControl: {
        maxWidth: 250,
        width: '100%',
        margin: '0 auto 20px',
        display: 'flex'
    },
    smallPrint: {
        fontSize: 13,
        textAlign: 'center',
        color: '#444',
        marginBottom: 10
    },
    zoneMenuItem: {
        whiteSpace: 'normal'
    },
    link: globalStyles.link
});

const validateForm = (
    passenger,
    service,
    ticket,
    serviceAvailability,
    useZones,
    disablePhoto,
    requireStudentId
) => {
    let stopSelectionValid = true;
    let dateValid = true;
    let timeValid = true;
    let availabilityValid = true;
    let yearGroupIsValid = true;
    let destinationValid = true;
    let zoneSelectionValid = true;

    if (service && 'stops' in service && !useZones) {
        stopSelectionValid = service.stops.includes(passenger.stop);
    }

    if (useZones) {
        const zone =
            passenger.zone &&
            service.zones.find(
                serviceZone => serviceZone.name === passenger.zone
            );

        if (zone && (!zone.stops || !zone.stops.length)) {
            zoneSelectionValid = true;
        } else if (
            zone &&
            zone.stops &&
            zone.stops.includes(passenger.zoneStop)
        ) {
            zoneSelectionValid = true;
        } else {
            zoneSelectionValid = false;
        }
    }

    if (service && service.destinations && service.destinations.length) {
        destinationValid = service.destinations.includes(passenger.dest);
    }

    if (ticket && ticket.type === 'single') {
        dateValid = passenger.startDate;
        timeValid =
            passenger.startTime === 'am' || passenger.startTime === 'pm';
        if (dateValid && timeValid) {
            availabilityValid = isDateAvailable(
                ticket,
                serviceAvailability,
                passenger.startDate,
                passenger.startTime
            );
        }
    }

    if (service && 'yr_groups' in service) {
        const yearGroups = getYearGroups(service.yr_groups);

        if (yearGroups.length) {
            yearGroupIsValid = yearGroups.includes(passenger.yr);
        }
    }

    if (
        ticket &&
        ((ticket.type === 'return' && ticket.sub_type !== 'fixed') ||
            ticket.type === '4_week' ||
            ticket.type === 'week')
    ) {
        dateValid = passenger.startDate;
        if (dateValid) {
            availabilityValid = isDateAvailable(
                ticket,
                serviceAvailability,
                passenger.startDate,
                passenger.startTime
            );
        }
    }

    const isPhotoValid = disablePhoto === true || passenger.photo;

    const isIdValid = !requireStudentId || (requireStudentId && passenger.id);

    return (
        stopSelectionValid &&
        destinationValid &&
        passenger.firstName &&
        passenger.lastName &&
        isPhotoValid &&
        dateValid &&
        timeValid &&
        availabilityValid &&
        yearGroupIsValid &&
        zoneSelectionValid &&
        isIdValid
    );
};

const availablilityStatement = availability => {
    if (availability === null) {
        return <></>;
    }

    if (availability <= 0) {
        return (
            <p
                style={{
                    textAlign: 'center',
                    color: 'red'
                }}
            >
                There are no tickets remaining on this date.
            </p>
        );
    }

    if (availability < 10) {
        return (
            <p
                style={{
                    textAlign: 'center',
                    color: 'red'
                }}
            >
                Hurry, only {availability} tickets available!
            </p>
        );
    }

    if (availability > 0) {
        return (
            <p
                style={{
                    textAlign: 'center',
                    color: 'green'
                }}
            >
                Tickets available.
            </p>
        );
    }

    return <></>;
};

const isAdhocTicket = ticket => {
    if (
        ticket &&
        (ticket.type === 'single' ||
            (ticket.type === 'return' && ticket.sub_type !== 'fixed') ||
            ticket.type === 'week' ||
            ticket.type === '4_week')
    ) {
        return true;
    }
    return false;
};

const PassengerForm = ({
    handleChange,
    handleDateChange,
    handleSubmit,
    handleImageChange,
    passenger,
    service,
    serviceAvailability,
    ticket,
    imagePreviewUrl,
    classes,
    disablePhoto,
    requireStudentId
}) => {
    const shouldDisableDateFn = isUnavailableFn(
        ticket,
        serviceAvailability,
        passenger
    );
    const availability = calc(
        ticket,
        serviceAvailability,
        passenger.startDate,
        passenger.startTime
    );

    let yearGroups = [];

    if (service) {
        yearGroups = getYearGroups(service.yr_groups);
    }

    const useZones =
        service.zones && ticket.pricing_model === 'Zonal' && ticket.zone_prices;

    const showDestination =
        service && service.destinations && service.destinations.length > 0;

    const availablilityEl = availablilityStatement(availability);
    const isValid = validateForm(
        passenger,
        service,
        ticket,
        serviceAvailability,
        useZones,
        disablePhoto,
        requireStudentId
    );

    const isRegularReturn =
        ticket && ticket.type === 'return' && ticket.sub_type !== 'fixed';

    if (!isAnyAvailable(ticket, serviceAvailability, moment())) {
        return (
            <div className={classes.root}>
                <ContentHeader text="No availability" id="passenger-heading" />
                <p
                    style={{
                        padding: '0 10px',
                        textAlign: 'center'
                    }}
                    variant="body2"
                >
                    This type of ticket is currently unavailable.
                </p>
                <p
                    style={{
                        padding: '0 10px 20px',
                        textAlign: 'center'
                    }}
                    variant="body2"
                >
                    Please check again soon or contact your transport operator
                    directly for more information.
                </p>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit}>
            {isAdhocTicket(ticket) && (
                <MuiPickersUtilsProvider utils={MomentUtils}>
                    <div className={classes.root} style={{ marginBottom: 30 }}>
                        <ContentHeader
                            text="Check availability"
                            id="availability-heading"
                        />
                        <div className={classes.form}>
                            {ticket && ticket.type === 'single' && (
                                <>
                                    <FormControl
                                        variant="outlined"
                                        className={classes.formControl}
                                        style={{ marginTop: 20 }}
                                    >
                                        <InputLabel
                                            style={{ background: 'white' }}
                                        >
                                            Journey time
                                        </InputLabel>
                                        <Select
                                            id="startTime"
                                            name="startTime"
                                            value={passenger.startTime}
                                            onChange={handleChange}
                                            variant="outlined"
                                            input={
                                                <OutlinedInput
                                                    name="startTime"
                                                    labelWidth={0}
                                                    id="outlined-start-time"
                                                />
                                            }
                                        >
                                            <MenuItem key="start-am" value="am">
                                                AM - morning
                                            </MenuItem>
                                            <MenuItem key="start-pm" value="pm">
                                                PM - afternoon
                                            </MenuItem>
                                        </Select>
                                    </FormControl>
                                    <div className={classes.datePicker}>
                                        <DatePicker
                                            autoOk
                                            value={passenger.startDate}
                                            onChange={handleDateChange}
                                            minDate={new Date()}
                                            inputVariant="outlined"
                                            label="Journey date"
                                            format="ddd Do MMM YYYY"
                                            maxDate={moment().add(7, 'days')}
                                            shouldDisableDate={
                                                shouldDisableDateFn
                                            }
                                        />
                                    </div>
                                    {availablilityEl}
                                    <Typography
                                        className={classes.smallPrint}
                                        variant="body2"
                                    >
                                        <strong>NOTE:</strong> Single tickets
                                        can only be purchased up to one week in
                                        advance.
                                    </Typography>
                                </>
                            )}
                            {isRegularReturn && (
                                <>
                                    <div
                                        style={{ marginTop: 20 }}
                                        className={classes.datePicker}
                                    >
                                        <DatePicker
                                            autoOk
                                            value={passenger.startDate}
                                            onChange={handleDateChange}
                                            minDate={new Date()}
                                            inputVariant="outlined"
                                            label="Return journey date"
                                            format="ddd Do MMM YYYY"
                                            maxDate={moment().add(7, 'days')}
                                            shouldDisableDate={
                                                shouldDisableDateFn
                                            }
                                        />
                                    </div>
                                    {availablilityEl}
                                    <Typography
                                        className={classes.smallPrint}
                                        variant="body2"
                                    >
                                        <strong>NOTE:</strong> Return tickets
                                        can only be purchased up to one week in
                                        advance.
                                    </Typography>
                                </>
                            )}
                            {ticket && ticket.type === 'week' && (
                                <>
                                    <div
                                        style={{ marginTop: 20 }}
                                        className={classes.datePicker}
                                    >
                                        <DatePicker
                                            autoOk
                                            value={passenger.startDate}
                                            onChange={handleDateChange}
                                            minDate={new Date()}
                                            maxDate={moment().add(7, 'days')}
                                            inputVariant="outlined"
                                            label="Ticket start date"
                                            format="ddd Do MMM YYYY"
                                            shouldDisableDate={
                                                shouldDisableDateFn
                                            }
                                        />
                                    </div>
                                    {availablilityEl}
                                    <Typography
                                        className={classes.smallPrint}
                                        variant="body2"
                                    >
                                        <strong>NOTE:</strong> Week tickets can
                                        only be purchased up to one week in
                                        advance.
                                    </Typography>
                                </>
                            )}
                            {ticket && ticket.type === '4_week' && (
                                <>
                                    <div
                                        style={{ marginTop: 20 }}
                                        className={classes.datePicker}
                                    >
                                        <DatePicker
                                            autoOk
                                            value={passenger.startDate}
                                            onChange={handleDateChange}
                                            minDate={new Date()}
                                            maxDate={moment().add(7, 'days')}
                                            inputVariant="outlined"
                                            label="Ticket start date"
                                            format="ddd Do MMM YYYY"
                                            shouldDisableDate={
                                                shouldDisableDateFn
                                            }
                                        />
                                    </div>
                                    {availablilityEl}
                                    <Typography
                                        className={classes.smallPrint}
                                        variant="body2"
                                    >
                                        <strong>NOTE:</strong> Four week tickets
                                        can only be purchased up to one week in
                                        advance.
                                    </Typography>
                                </>
                            )}
                        </div>
                    </div>
                </MuiPickersUtilsProvider>
            )}
            {useZones && (
                <div className={classes.root} style={{ marginBottom: 30 }}>
                    <ContentHeader
                        text="Zone selection"
                        id="availability-heading"
                    />
                    <Typography className={classes.description} variant="body2">
                        Select your travel zone.{' '}
                        {service.timetable_url && (
                            <>
                                <a
                                    href={service.timetable_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={classes.link}
                                >
                                    Help finding your zone
                                </a>
                                .
                            </>
                        )}
                    </Typography>
                    <div className={classes.form}>
                        <ZonesForm
                            classes={classes}
                            passenger={passenger}
                            service={service}
                            ticket={ticket}
                            handleChange={handleChange}
                            zone={passenger.zone}
                            zoneStop={passenger.zoneStop}
                        />
                    </div>
                </div>
            )}
            <div className={classes.root}>
                <ContentHeader text="Add passenger" id="passenger-heading" />
                <Typography className={classes.description} variant="body2">
                    Please confirm the details of the passenger this ticket is
                    for:
                </Typography>
                <div className={classes.form}>
                    <div className={classes.name}>
                        <TextField
                            autoFocus
                            autoComplete="off"
                            className={`${classes.input} input`}
                            id="firstName"
                            name="firstName"
                            type="text"
                            label="First name"
                            value={passenger.firstName}
                            onChange={handleChange}
                            variant="outlined"
                        />
                        <TextField
                            autoComplete="off"
                            className={`${classes.input} input`}
                            id="lastName"
                            name="lastName"
                            type="text"
                            label="Last name"
                            value={passenger.lastName}
                            onChange={handleChange}
                            variant="outlined"
                        />
                        {requireStudentId && (
                            <TextField
                                autoComplete="off"
                                className={`${classes.input} input`}
                                id="id"
                                name="id"
                                type="text"
                                label="Student ID"
                                value={passenger.id}
                                onChange={handleChange}
                                variant="outlined"
                            />
                        )}
                    </div>
                    {service && 'stops' in service && !useZones && (
                        <FormControl
                            variant="outlined"
                            className={classes.formControl}
                        >
                            <InputLabel style={{ background: 'white' }}>
                                Bus stop
                            </InputLabel>
                            <Select
                                id="stop"
                                name="stop"
                                value={passenger.stop}
                                onChange={handleChange}
                                variant="outlined"
                                input={
                                    <OutlinedInput
                                        name="stop"
                                        labelWidth={0}
                                        id="outlined-stop"
                                    />
                                }
                            >
                                {service.stops.map(serviceStop => (
                                    <MenuItem
                                        key={`stop-${serviceStop}`}
                                        value={serviceStop}
                                    >
                                        {serviceStop}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                    {showDestination && (
                        <FormControl
                            variant="outlined"
                            className={classes.formControl}
                        >
                            <InputLabel style={{ background: 'white' }}>
                                Destination
                            </InputLabel>
                            <Select
                                id="dest"
                                name="dest"
                                value={passenger.dest}
                                onChange={handleChange}
                                variant="outlined"
                                input={
                                    <OutlinedInput
                                        name="dest"
                                        labelWidth={0}
                                        id="outlined-dest"
                                    />
                                }
                            >
                                {service.destinations.map(serviceDest => (
                                    <MenuItem
                                        key={`dest-${serviceDest}`}
                                        value={serviceDest}
                                    >
                                        {serviceDest}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                    {yearGroups.length > 0 && (
                        <FormControl
                            variant="outlined"
                            className={classes.formControl}
                        >
                            <InputLabel style={{ background: 'white' }}>
                                Year group
                            </InputLabel>
                            <Select
                                id="yr"
                                name="yr"
                                value={passenger.yr}
                                onChange={handleChange}
                                variant="outlined"
                                input={
                                    <OutlinedInput
                                        name="yr"
                                        labelWidth={0}
                                        id="outlined-yr"
                                    />
                                }
                            >
                                {yearGroups.map(year => (
                                    <MenuItem key={`year-${year}`} value={year}>
                                        {year === 'reception'
                                            ? 'Reception'
                                            : `Year ${year}`}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                    <div className={classes.imageUpload}>
                        {!disablePhoto && (
                            <>
                                <div className={classes.imagePreview}>
                                    {imagePreviewUrl ? (
                                        <img
                                            src={imagePreviewUrl}
                                            alt="Ticket user"
                                        />
                                    ) : (
                                        <div
                                            className={
                                                classes.imagePreviewPlaceholder
                                            }
                                        >
                                            <Typography variant="body2">
                                                Passenger photo
                                                <span
                                                    className={
                                                        classes.imagePreviewHelp
                                                    }
                                                >
                                                    Please provide a clear
                                                    passport style headshot of
                                                    the&nbsp;passenger,
                                                    including only head and
                                                    shoulders.
                                                </span>
                                            </Typography>
                                        </div>
                                    )}
                                    <Typography variant="body2">
                                        <a
                                            className={classes.photoHelp}
                                            href="https://help.shuttleid.uk/en/articles/7852186-how-to-take-and-upload-your-passenger-photo"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            I need help with my photo
                                        </a>
                                    </Typography>
                                </div>
                                <label htmlFor="photo">
                                    <input
                                        accept="image/jpeg, image/png"
                                        className={classes.upload}
                                        id="photo"
                                        type="file"
                                        onChange={handleImageChange}
                                    />
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        component="span"
                                        className={`${classes.button} ${
                                            classes.uploadButton
                                        }`}
                                        id="uploadPhoto"
                                    >
                                        {passenger.photo && (
                                            <span>Change Photo</span>
                                        )}
                                        {!passenger.photo && (
                                            <span>Upload Photo</span>
                                        )}
                                    </Button>
                                </label>
                            </>
                        )}
                        <Button
                            variant="outlined"
                            color="primary"
                            disabled={!isValid}
                            className={`${classes.button} ${
                                classes.confirmButton
                            }`}
                            type="submit"
                            id="submitPassenger"
                        >
                            {!isValid ? (
                                <span>&#x1F6AB;</span>
                            ) : (
                                <span>&#x2705;</span>
                            )}
                            &nbsp;Confirm
                        </Button>
                    </div>
                </div>
            </div>
        </form>
    );
};

PassengerForm.propTypes = {
    handleChange: PropTypes.func.isRequired,
    handleSubmit: PropTypes.func.isRequired,
    handleDateChange: PropTypes.func,
    handleImageChange: PropTypes.func.isRequired,
    passenger: PropTypes.shape({
        firstName: PropTypes.string,
        lastName: PropTypes.string,
        stop: PropTypes.string,
        photo: PropTypes.object
    }).isRequired,
    service: PropTypes.shape().isRequired,
    imagePreviewUrl: PropTypes.string,
    ticket: PropTypes.shape({
        type: PropTypes.string
    }),
    disablePhoto: PropTypes.bool,
    requireStudentId: PropTypes.bool,
    classes: PropTypes.shape().isRequired
};

PassengerForm.defaultProps = {
    imagePreviewUrl: '',
    ticket: null,
    disablePhoto: false,
    requireStudentId: false,
    handleDateChange: () => {}
};

export default withStyles(styles)(PassengerForm);
